import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CaretRight, Door, Plus, Ruler } from '@phosphor-icons/react';
import { FeldRaster, ImmoButton, TextFeld } from '../../../../components/immobilien/ui';
import { supabase } from '../../../../lib/supabase/client';
import { einheitArt, einheitenUebersicht, type EinheitDetailRoh, type EinheitUebersicht, type MieterDetailRoh } from '../../../../lib/immobilien/objektDetail';
import { eur, heuteIso, qm } from '../../../../lib/immobilien/format';
import { parseDezimal } from '../../../../lib/immobilien/stammdaten';
import { meldeObjektGeaendert } from '../../../../lib/immobilien/useObjektKontext';
import { zimmerBezeichnung } from '../../../../lib/immobilien/zimmer';
import type { EinheitRoh } from '../../../../lib/immobilien/useEinheitDetail';

type Zustand =
  | { status: 'laedt' }
  | { status: 'fehler'; meldung: string }
  | { status: 'ok'; zimmer: EinheitUebersicht[]; bezeichnungen: string[]; wohnung: { id: string; bezeichnung: string } | null };

const MIETER_SPALTEN = 'id, einheit_id, vorname, nachname, firma_name, status, kaltmiete, nebenkosten_vorauszahlung, heizkosten, weitere_kosten, miet_historie, mietbeginn, mietende';

/**
 * #499: Zimmer einer Wohnung (einzeln vermietbar, z. B. WG). Jedes Zimmer ist eine eigene Einheit
 * mit Mieter, Vertrag, Miete und Kaution. Auf einem Zimmer: Rückweg zur Wohnung.
 */
export function EinheitZimmer({ objektId, einheit, anlegenSignal }: { objektId: string; einheit: EinheitRoh; /** steigt bei „Zimmer anlegen“ im Kopfmenü */ anlegenSignal: number }) {
  const navigate = useNavigate();
  const wohnungId = einheit.uebergeordnete_einheit_id ?? null;
  const istWohnung = !wohnungId && einheitArt(einheit.typ, einheit.bezeichnung) === 'wohnung';
  const [zustand, setZustand] = useState<Zustand>({ status: 'laedt' });
  const [formular, setFormular] = useState(false);
  const [bez, setBez] = useState('');
  const [flaeche, setFlaeche] = useState('');
  const [speichert, setSpeichert] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  const lade = useCallback(async () => {
    if (wohnungId) {
      const { data, error } = await supabase.from('objekt_einheiten').select('id, bezeichnung').eq('id', wohnungId).maybeSingle();
      if (error) { setZustand({ status: 'fehler', meldung: error.message }); return; }
      const w = data as { id: string; bezeichnung: string | null } | null;
      setZustand({ status: 'ok', zimmer: [], bezeichnungen: [], wohnung: w ? { id: w.id, bezeichnung: (w.bezeichnung ?? '').trim() || 'Wohnung' } : null });
      return;
    }
    const [alle, zimmer] = await Promise.all([
      supabase.from('objekt_einheiten').select('bezeichnung').eq('objekt_id', objektId),
      supabase.from('objekt_einheiten').select('id, bezeichnung, typ, wohnflaeche, nutzflaeche, zimmer, etage, haus_id, status').eq('uebergeordnete_einheit_id', einheit.id),
    ]);
    const err = alle.error ?? zimmer.error;
    if (err) { setZustand({ status: 'fehler', meldung: err.message }); return; }
    const zeilen = (zimmer.data ?? []) as EinheitDetailRoh[];
    let mieter: MieterDetailRoh[] = [];
    if (zeilen.length) {
      const m = await supabase.from('mieter').select(MIETER_SPALTEN).in('einheit_id', zeilen.map(z => z.id));
      if (m.error) { setZustand({ status: 'fehler', meldung: m.error.message }); return; }
      mieter = (m.data ?? []) as MieterDetailRoh[];
    }
    setZustand({
      status: 'ok',
      zimmer: einheitenUebersicht(zeilen, mieter, heuteIso()),
      bezeichnungen: ((alle.data ?? []) as { bezeichnung: string | null }[]).map(a => (a.bezeichnung ?? '').trim()),
      wohnung: null,
    });
  }, [objektId, einheit.id, wohnungId]);

  useEffect(() => { if (wohnungId || istWohnung) void lade(); }, [lade, wohnungId, istWohnung]);
  const bereit = zustand.status === 'ok';
  useEffect(() => { if (anlegenSignal > 0 && bereit) { setFehler(null); setBez(''); setFlaeche(''); setFormular(true); } }, [anlegenSignal, bereit]);

  if (!wohnungId && !istWohnung) return null;
  if (zustand.status === 'laedt') return null;
  if (zustand.status === 'fehler') {
    return <span role="alert" style={{ fontSize: 13, color: 'var(--bad)' }}>Zimmer konnten nicht geladen werden: {zustand.meldung}</span>;
  }

  const zuEinheit = (id: string) => navigate(`/admin/objekte/${encodeURIComponent(objektId)}/einheiten/${encodeURIComponent(id)}`);

  // Auf einem Zimmer: Rückweg zur Wohnung
  if (wohnungId) {
    if (!zustand.wohnung) return null;
    const w = zustand.wohnung;
    return (
      <button type="button" onClick={() => zuEinheit(w.id)} className="immo-glas immo-hover-widget flex items-center cursor-pointer text-left"
        style={{ borderRadius: 20, padding: '12px 16px', gap: 10, border: 'none', color: 'var(--ink)' }}>
        <Door weight="fill" size={18} color="var(--acc)" />
        <span style={{ fontSize: 13 }}>Zimmer der Wohnung <b>{w.bezeichnung}</b></span>
        <CaretRight weight="bold" size={12} color="var(--ink3)" style={{ marginLeft: 'auto' }} />
      </button>
    );
  }

  const bezeichnungen = zustand.bezeichnungen;
  const wohnungName = (einheit.bezeichnung ?? '').trim() || 'Wohnung';
  const vorschlag = zimmerBezeichnung(wohnungName, bezeichnungen);
  const oeffne = () => { setBez(''); setFlaeche(''); setFehler(null); setFormular(true); };

  const anlegen = async () => {
    const name = bez.trim() || vorschlag;
    if (bezeichnungen.some(b => b.toLowerCase() === name.toLowerCase())) { setFehler(`„${name}“ gibt es in diesem Objekt schon.`); return; }
    const f = flaeche.trim() ? parseDezimal(flaeche) : null;
    if (flaeche.trim() && f === null) { setFehler('Bitte eine gültige Fläche angeben.'); return; }
    setSpeichert(true);
    setFehler(null);
    const { error } = await supabase.from('objekt_einheiten').insert({
      objekt_id: objektId, uebergeordnete_einheit_id: einheit.id, bezeichnung: name, typ: 'Zimmer',
      wohnflaeche: f, etage: einheit.etage ?? null, status: 'leerstand',
    });
    setSpeichert(false);
    if (error) { setFehler(error.message); return; }
    setFormular(false);
    meldeObjektGeaendert(objektId);
    await lade();
  };

  const zimmer = zustand.zimmer;
  const vermietet = zimmer.filter(z => z.vermietet).length;
  // Wohnungen ohne Zimmer: Karte erst nach „Zimmer anlegen“ im Kopfmenü
  if (zimmer.length === 0 && !formular) return null;

  return (
    <section aria-label="Zimmer" className="immo-glas flex flex-col" style={{ borderRadius: 24, padding: 16, gap: 12 }}>
      <div className="flex items-center justify-between flex-wrap" style={{ gap: 10 }}>
        <span className="flex flex-col min-w-0">
          <span style={{ fontSize: 15, fontWeight: 800 }}>{zimmer.length ? `Zimmer (${vermietet}/${zimmer.length} vermietet)` : 'Zimmer einzeln vermieten'}</span>
          <span style={{ fontSize: 12, color: 'var(--ink3)' }}>
            {zimmer.length ? 'Jedes Zimmer hat eigenen Mieter, Vertrag, Miete und Kaution.' : 'Z. B. WG: Zimmer als eigene Einheiten mit eigenem Mieter, Vertrag, Miete und Kaution.'}
          </span>
        </span>
        {!formular && <ImmoButton art={zimmer.length ? 'fuellung' : 'akzent'} icon={Plus} onClick={oeffne}>Zimmer anlegen</ImmoButton>}
      </div>

      {zimmer.length > 0 && (
        <div className="flex flex-col">
          {zimmer.map(z => (
            <button key={z.id} type="button" onClick={() => zuEinheit(z.id)} className="immo-hover-flaeche grid items-center cursor-pointer text-left"
              style={{ gridTemplateColumns: 'minmax(0,1.4fr) minmax(0,1.6fr) minmax(0,0.8fr) 14px', gap: 10, padding: '10px 6px', border: 'none', borderTop: '1px solid var(--line)', background: 'transparent', fontSize: 13, color: 'var(--ink)' }}>
              <span className="flex flex-col min-w-0"><b className="truncate">{z.bezeichnung}</b><span style={{ fontSize: 11, color: 'var(--ink3)' }}>{z.flaeche > 0 ? qm(z.flaeche) : 'Fläche –'}</span></span>
              <span className="truncate" style={{ color: z.vermietet ? 'var(--ink)' : 'var(--warn)', fontWeight: z.vermietet ? 400 : 700 }}>{z.vermietet ? z.mieter.join(', ') : 'frei'}</span>
              <span className="immo-zahl" style={{ fontWeight: 700 }}>{z.vermietet ? eur(z.gesamt) : '–'}</span>
              <CaretRight weight="bold" size={12} color="var(--ink3)" />
            </button>
          ))}
        </div>
      )}

      {formular && (
        <div className="flex flex-col" style={{ gap: 10, paddingTop: 12, borderTop: '1px solid var(--line)' }}>
          <FeldRaster minBreite={200}>
            <TextFeld label="Bezeichnung" icon={Door} wert={bez} onChange={setBez} placeholder={vorschlag} />
            <TextFeld label="Fläche (m², optional)" icon={Ruler} wert={flaeche} onChange={setFlaeche} inputMode="decimal" placeholder="z. B. 14,5" />
          </FeldRaster>
          {fehler && <span role="alert" style={{ fontSize: 13, fontWeight: 700, color: 'var(--bad)' }}>{fehler}</span>}
          <div className="flex flex-wrap" style={{ gap: 8 }}>
            <ImmoButton art="akzent" icon={Plus} disabled={speichert} onClick={() => { void anlegen(); }}>{speichert ? 'Wird angelegt …' : 'Zimmer anlegen'}</ImmoButton>
            <ImmoButton onClick={() => setFormular(false)} disabled={speichert}>Abbrechen</ImmoButton>
          </div>
        </div>
      )}
    </section>
  );
}
